<script setup lang="ts">
import type { RouterMeta } from '@/router';
import { RouterView, useRoute } from 'vue-router';
import { computed, watch } from 'vue';
import { useRouteQueryStore } from './stores/route-query.store';
import { useAbility } from '@casl/vue';
import { useI18n } from 'vue-i18n';
import * as Sentry from '@sentry/vue';
import { posthogClient } from '@/utils/posthog';
import Alert from '@/components/Alert.vue';
import { useKeyboardSettings } from './hooks/use-keyboard-settings';
import { useAbilities, useTokenPayload } from '@/queries/auth/use-login.ts';
import { setProdMutationGuardCurrentUserIsDeveloper } from '@/utils/prod-mutation-guard';
import PdfPreviewHost from '@/components/PdfPreviewHost.vue';
import CommandPalette from '@/components/command-palette/CommandPalette.vue';
import PriceChangeConfirmationProvider from '@/components/price-change-confirmation/PriceChangeConfirmationProvider.vue';
import type { JWTUser } from '@/entities/auth/jwt-user.entity';
import { useIsDeveloper } from '@/hooks/use-is-developer';
import { evidenceUploadQueue } from '@/utils/evidence-upload-queue';

const route = useRoute();
const routeQueryStore = useRouteQueryStore();
const ability = useAbility();

const isAuthBootstrapEnabled = computed(() => !(route.meta as RouterMeta).public);
const { data: tokenPayload, isFetching } = useTokenPayload(isAuthBootstrapEnabled);
const jwtUser = computed<JWTUser | null>(() =>
  tokenPayload.value && isJWTUser(tokenPayload.value) ? tokenPayload.value : null,
);
useAbilities(computed(() => !!jwtUser.value));

useKeyboardSettings();

const isDeveloper = useIsDeveloper();
watch(isDeveloper, (value) => setProdMutationGuardCurrentUserIsDeveloper(value), {
  immediate: true,
});

void evidenceUploadQueue.start();

watch(jwtUser, async () => {
  evidenceUploadQueue.setUser(jwtUser.value?.id ?? null);

  Sentry.setUser({
    id: jwtUser.value?.id,
    email: jwtUser.value?.email,
    role: jwtUser.value?.role,
  });

  if (!jwtUser.value) {
    posthogClient.reset();
    ability.update([]);
    return;
  }
  posthogClient.identify(jwtUser.value.id, {
    email: jwtUser.value.email,
    name: `${jwtUser.value.firstName} ${jwtUser.value.lastName}`,
  });
});

watch(
  () => route.query,
  async () => {
    await routeQueryStore.replace(route.query);
  },
);

const { t } = useI18n();

function isJWTUser(payload: unknown): payload is JWTUser {
  return !!payload && typeof payload === 'object' && 'role' in payload;
}
</script>

<template>
  <PriceChangeConfirmationProvider>
    <RouterView />
  </PriceChangeConfirmationProvider>
  <div
    v-if="isFetching && isAuthBootstrapEnabled"
    class="flex h-screen w-full animate-fade-in flex-col items-center justify-center gap-6 p-6"
  >
    <Spinner class="h-24 w-24 text-hazel-500" :stroke-width="1" />
    <div class="text-center text-base text-primary">
      {{ t('loadingUser') }}
    </div>
    <div class="text-center text-sm text-gray-500">
      {{ t('loadingUserDescription') }}
    </div>
  </div>

  <!-- Global Alert Component -->
  <Alert />
  <PdfPreviewHost />
  <CommandPalette />
</template>
<style>
@import '@/../node_modules/@vueform/multiselect/themes/tailwind.css';

@keyframes fade-in {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

.animate-fade-in {
  animation: fade-in 2s 1s;
  animation-fill-mode: backwards;
}
</style>
<i18n lang="json">
{
  "en": {
    "loadingUser": "Loading user...",
    "loadingUserDescription": "There might be a network issue. Please wait a moment."
  },
  "de": {
    "loadingUser": "Lade Benutzer...",
    "loadingUserDescription": "Eventuell handelt es sich um ein Netzwerkproblem. Bitte warten Sie einen Moment."
  },
  "es": {
    "loadingUser": "Cargando usuario…",
    "loadingUserDescription": "Puede haber un problema de red. Espere un momento."
  }
}
</i18n>
